/**
 * WorkflowCreateDialog - new-workflow form.
 *
 * Lives inside WorkflowsPage. Fields are key (snake_case, immutable
 * after create), label, and description. On success the parent selects
 * the new workflow automatically so the user lands on it.
 *
 * Uses `useForm` per CLAUDE.md (Hand-Rolled Forms Are Banned).
 */

import { useEffect, useMemo } from 'react'

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import { toSnakeLowercaseKey } from '@/admin/components/shared/attribute-configuration-utils'
import {
  useCreateWorkflow,
  type WorkflowCreateInput,
} from '@/admin/hooks/use-workflow-definitions'

interface WorkflowCreateDialogProps {
  open: boolean
  onOpenChange: (next: boolean) => void
  onCreated: (uuid: string) => void
  onError: (err: unknown) => void
}

export function WorkflowCreateDialog({
  open,
  onOpenChange,
  onCreated,
  onError,
}: WorkflowCreateDialogProps) {
  const create = useCreateWorkflow()

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'label',
        label: 'Label',
        placeholder: 'Registration lifecycle',
        value: '',
        required: 'Label is required',
      },
      {
        type: 'text',
        name: 'description',
        label: 'Description',
        placeholder: 'Optional description visible to other admins.',
        value: '',
      },
    ],
    [],
  )

  const { Form, ref } = useForm(
    {
      name: 'workflow-create-form',
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as unknown as WorkflowCreateInput
      try {
        const created = await create.mutateAsync({
          key: toSnakeLowercaseKey(values.label),
          label: values.label.trim(),
          description: (values.description ?? '').trim() || undefined,
        })
        onCreated(created.uuid)
      } catch (err) {
        onError(err)
      }
    },
    [open, fields],
  )

  useEffect(() => {
    if (!open) ref.current?.resetForm()
  }, [open, ref])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Create Workflow</DialogTitle>
        </DialogHeader>
        <Form className="flex flex-col gap-4 py-2" />
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => ref.current?.submitForm()}
            disabled={create.isPending}
            data-testid="workflows-create-submit"
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
